import React, { useState, useEffect, useRef } from 'react';
import { VehiclesClient } from '../web-api-client.ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faSearch, faTriangleExclamation, faTimes } from '@fortawesome/free-solid-svg-icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Modal, InputGroup, FormControl, Badge } from 'react-bootstrap';
import NewVehicle from './NewVehicle.js';
import { Form } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import BackButton from './BackButton.js';
import { isMobileDevice } from './utils/utils.js';
import ClearableInput from './ClearableInput.js';
import ScannerLauncher from './ScannerLauncher.js';
import Loader from './Loader.js';
import UserContext from '../providers/UserContext.js';



const Vehicles = ({ status: initialFilterStatus }) => {
  const { barcode } = useParams();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  //const [onlyUnderThreshold, setOnlyUnderThreshold] = useState(initialUnderThreshold || false);
  const [filterStatus, setFilterStatus] = useState(initialFilterStatus || false);
  const [sortColumn, setSortColumn] = useState('created');
  const [sortDirection, setSortDirection] = useState('desc');
  const [makes, setMakes] = useState([]);
  const [filtersReady, setFiltersReady] = useState(false);
  const [filters, setFilters] = useState({
    make: null,
    model: null,
    year: null,
    status: null,
  });
  const [searchParams] = useSearchParams();

  const hasLoadedRef = useRef(false);
  const user = React.useContext(UserContext);


  useEffect(() => {
    if (!hasLoadedRef.current) return;

    const newSearch = searchParams.get('search') || '';
    const newMake = parseInt(searchParams.get('make'), 10) || null;
    const newModel = parseInt(searchParams.get('model'), 10) || null;
    const newSort = searchParams.get('sort') || 'created';
    const newDir = searchParams.get('dir') || 'desc';
    const newStatus = parseInt(searchParams.get('status'), 10) || 0;
    const newYear = searchParams.get('year') || '';
    const newPage = parseInt(searchParams.get('page'), 10) || 1;

    setSearchTerm(newSearch);
    setFilters({ make: newMake, model: newModel, year: newYear, status: newStatus });
    setSortColumn(newSort);
    setSortDirection(newDir);
    setCurrentPage(newPage);

    populateVehicleData(newPage, {
      searchTerm: newSearch,
      filterStatus: newStatus,
      sortColumn: newSort,
      sortDirection: newDir,
      filters: {
        make: newMake,
        model: newModel,
        year: newYear,
        status: newStatus
      }
    });
  }, [searchParams]);


  useEffect(() => {
    if (hasLoadedRef.current) {
      populateVehicleData(currentPage, {
        searchTerm,
        sortColumn,
        sortDirection,
        filters
      });
    }
  }, [filters, sortColumn, sortDirection]);

  // Utility to build query string
  const buildQuery = () => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (filters.make) params.set('make', filters.make);
    if (filters.model) params.set('model', filters.model);
    if (filters.year) params.set('year', filters.year);
    if (filters.status) params.set('status', filters.status);
    params.set('sort', sortColumn);
    params.set('dir', sortDirection);
    params.set('page', currentPage);
    return `?${params.toString()}`;
  };
  const itemsPerPage = 15; // Adjust as needed
  const navigate = useNavigate();

  const handleRowClick = (vehicleId) => {
    navigate(`/vehicles/${vehicleId}${buildQuery()}`);
  };

  useEffect(() => {

    if (barcode) {
      setShowAddVehicleModal(true);
    }
    const fetchMakes = async () => {
      let client = new VehiclesClient();
      const makeData = await client.getMakes(null);
      setMakes(makeData);
    }
    fetchMakes();
  }, [currentPage, sortColumn, sortDirection]);

  useEffect(() => {
    navigate(buildQuery(), { replace: true });
  }, [searchTerm, filters, sortColumn, sortDirection, currentPage]);


  useEffect(() => {
    const fetchInitialData = async () => {
      let client = new VehiclesClient();
      const makesData = await client.getMakes(null);
      setMakes(makesData);

      const initialSearch = searchParams.get('search') || '';
      const initialMake = parseInt(searchParams.get('make'), 10) || null;
      const initialModel = parseInt(searchParams.get('model'), 10) || null;
      const initialYear = parseInt(searchParams.get('year'), 10) || null;
      const initialStatus = parseInt(searchParams.get('status'), 10) || null;
      const initialSort = searchParams.get('sort') || 'make';
      const initialDir = searchParams.get('dir') || 'asc';
      const initialPage = parseInt(searchParams.get('page'), 10) || 1;

      setSearchTerm(initialSearch);
      setFilters({ make: initialMake, model: initialModel, year: initialYear, status: initialStatus });
      setSortColumn(initialSort);
      setSortDirection(initialDir);
      setCurrentPage(initialPage);

      setFiltersReady(true);
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (filtersReady) {
      populateVehicleData(currentPage, {
        searchTerm,
        sortColumn,
        sortDirection,
        filters
      });
      hasLoadedRef.current = true;
    }
  }, [filtersReady]);

  const populateVehicleData = async (page, state) => {
    setLoading(true);
    let client = new VehiclesClient();

    const {
      searchTerm: s,
      sortColumn: sc,
      sortDirection: sd,
      filters: f
    } = state ?? {
      searchTerm,
      sortColumn,
      sortDirection,
      filters
    };

    const data = await client.getVehicleList(
      user.dealerId,
      page,
      itemsPerPage,
      searchTerm,
      sortColumn,
      sortDirection,
      filters.make,
      filters.model,
      filters.year,
      filters.status
    );

    setVehicles(data.items);
    setTotalPages(data.totalPages);
    setLoading(false);
  };


  const resetList = async () => {
    setLoading(true);
    let client = new VehiclesClient();
    const data = await client.getVehicleList(1, 1, itemsPerPage, '', 'created', 'desc', null, null, null, null);
    setVehicles(data.items);
    setLoading(false);
  }

  const handleAddVehicle = () => {
    setShowAddVehicleModal(true);
  };

  const closeAddVehicleModal = () => {
    setShowAddVehicleModal(false);
    populateVehicleData(currentPage);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };
  const handleScan = (barcode) => {
    setSearchTerm(barcode);
  }

  const handleSearch = async (e) => {
    setLoading(true);
    let client = new VehiclesClient();
    const data = await client.getVehicleList(1, 1, itemsPerPage, searchTerm, sortColumn, sortDirection, filters.make, filters.model, filters.year, filters.status);
    setVehicles(data.items);
    setLoading(false);
  };
  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page on sort
  };
  useEffect(() => {
    if (searchTerm) {
      handleSearch(searchTerm);
    }
  }, [searchTerm]);


  return (
    <div className="container mb-5" style={{ "marginBottom": '90px!important' }}>
      <BackButton />
      <h1>Vehicles</h1>

      <div className="row g-2 align-items-center">
        <div className="col-12 col-md-8 d-flex flex-wrap align-items-center gap-3">
          <InputGroup style={{ maxWidth: '300px' }}>
            <ClearableInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onEnter={handleSearch}
              onClear={resetList}
            />
            <ScannerLauncher onBarcodeDetected={handleScan} mode="button" buttonClasses="ms-2" />

          </InputGroup>

          {/*  <Form.Check
            type="switch"
            id="under-threshold-switch"
            label="Only Under Threshold"
            checked={onlyUnderThreshold}
            onChange={() => setOnlyUnderThreshold(!onlyUnderThreshold)}
            style={{ userSelect: 'none' }}
          />
          <Form.Check
            type="switch"
            id="under-threshold-switch"
            label="Only Pending"
            checked={onlyPending}
            onChange={() => setOnlyPending(!onlyPending)}
            style={{ userSelect: 'none' }}
          /> */}
        </div>

        <div className="col-12 col-md-4 d-flex justify-content-md-end">
          <button className="btn btn-primary w-100 w-md-auto" onClick={handleAddVehicle}>
            Add Vehicle
          </button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <table className="table table-hover align-middle">
          <thead>
            <tr>
              <th onClick={() => handleSort("stockNumber")} style={{ cursor: 'pointer' }}>
                Stock Number {sortColumn === 'stockNumber' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>

              <th onClick={() => handleSort("year")} style={{ cursor: 'pointer' }}>
                Year {sortColumn === 'year' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>

              <th onClick={() => handleSort("make")} style={{ cursor: 'pointer' }}>
                Make {sortColumn === 'make' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort("model")} style={{ cursor: 'pointer' }}>
                Model {sortColumn === 'model' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort("trim")} style={{ cursor: 'pointer' }}>
                Trim {sortColumn === 'trim' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort("color")} style={{ cursor: 'pointer' }}>
                Color {sortColumn === 'color' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>


              {!isMobileDevice() && (
                <th onClick={() => handleSort("vin")} style={{ cursor: 'pointer' }}>
                  Vin {sortColumn === 'vin' && (sortDirection === 'asc' ? '▲' : '▼')}
                </th>
              )}
              <th onClick={() => handleSort("status")} style={{ cursor: 'pointer' }}>
                Status {sortColumn === 'status' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>

            </tr>
            <tr>
              <td></td>
              <td></td>
              <td>
                <InputGroup size="sm">
                  <Form.Select
                    value={filters.make}
                    onChange={(e) => {
                      setFilters((prev) => ({ ...prev, make: e.target.value }));
                      setCurrentPage(1);
                    }}
                  >
                    <option value="">All</option>
                    {makes.map((m) => (
                      <option key={m.id} value={m.label}>{m.label}</option>
                    ))}
                  </Form.Select>
                </InputGroup>
              </td>
              <td></td>
              <td></td>
              <td></td>
              {!isMobileDevice() && <td></td>}
              <td></td>
            </tr>

          </thead>
          <tbody>
            {vehicles.map(vehicle => (
              <tr key={vehicle.id} onClick={() => handleRowClick(vehicle.id)} style={{ cursor: 'pointer' }}>
                <td>{vehicle.stockNumber}</td>
                <td>{vehicle.year}</td>
                <td>{vehicle.makeName}</td>
                <td>{vehicle.modelName}</td>
                <td>{vehicle.trim}</td>
                <td>{vehicle.color}</td>
                {!isMobileDevice() && <td>{vehicle.vin}</td>}
                <td>{vehicle.status}</td>


              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Pagination */}
      <nav className='mb-6'>
        <ul className="pagination justify-content-end">
          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>Previous</button>
          </li>
          {Array.from({ length: totalPages }, (_, index) => (
            <li key={index} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(index + 1)}>
                {index + 1}
              </button>
            </li>
          ))}
          <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
            <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>Next</button>
          </li>
        </ul>
      </nav>

      {/* Add Vehicle Modal */}
      <Modal show={showAddVehicleModal} onHide={closeAddVehicleModal} size="lg" scrollable centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>New Vehicle</Modal.Title>
        </Modal.Header>
        <NewVehicle barcode={barcode} />
      </Modal>
    </div>
  );
};

export default Vehicles;
